import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { Button, Toast } from 'antd-mobile';
import { Header } from '../components/Header';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import { TmsWaypointStepIndicator } from './components/TmsWaypointStepIndicator';
import { getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { PdaPath } from '../../../enums/PdaNavigatePathEnum';
import { WaypointType, TmsTaskContainerType, TmsOperationSourceEnum, TmsTaskWaypointNodeType } from '../../../enums/TmsEnum';
import { useEffect, useState } from 'react';
import { TmsCountedInput } from './components/TmsCountedInput';
import MultiPhotoGalleryView from '../components/MultiPhotoGalleryView';
import { pdaOpenCamera, startPrepareCameraLocation, FirstPhotoWatermarkLocation } from '../../../utils/appRuntime';
import { useSaveTmsTaskWaypointLocationInfo } from './hooks/useSaveTmsTaskWaypointLocationInfo';
import { getCurrentServerTimeMillis } from '../../../utils/pdaCameraUtils';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../../graphql/queries/warehouseQueries';
import { fileUpload } from '../../../utils/storageUtils';
import { base64ToFile } from '../../../services/DeliveryDataService';
import {
  GET_TMS_UNLOADING_INFO_SUMMARY,
  GetTmsUnloadingInfoSummaryResponse,
  TmsUnloadingInfoDestinationGroup,
  GET_TMS_TASK_WAYPOINT_BOL_INFO,
  GetTmsTaskWaypointBolInfoResponse,
  SAVE_UNLOADING_DETAIL_INFO,
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';

const kMaxBolPhotoLength = 3;
const kMinBolPhotoLength = 1;

function getContainerTypeName(type?: number | null): string {
  switch (type) {
    case TmsTaskContainerType.CARTON:
      return t('pda.pages.loadingDetail.containerTypeCardboardBox') || '卡板箱';
    case TmsTaskContainerType.BAG:
      return t('pda.pages.loadingDetail.containerTypeSnakeSkinBag') || '蛇皮袋';
    case TmsTaskContainerType.PALLET:
      return t('pda.pages.loadingDetail.containerTypePallet') || '托盘';
    case TmsTaskContainerType.GAYLORD:
      return t('pda.pages.loadingDetail.containerTypeGaylord') || 'Gaylord';
    default:
      return '-';
  }
}

export default function TmsUnloadingInfoPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const state = (location.state as TmsPageState) || null;

  const taskId = state?.taskId ? parseInt(state.taskId) : NaN;
  const taskWayPointId = state?.taskWaypointId ? parseInt(state.taskWaypointId) : NaN;
  const isValidTaskId = !isNaN(taskId) && !isNaN(taskWayPointId);
  const isDestination = state?.task?.waypointType === WaypointType.DESTINATION;

  const { data, loading, error } = useQuery<GetTmsUnloadingInfoSummaryResponse>(
    GET_TMS_UNLOADING_INFO_SUMMARY,
    {
      variables: { taskId, taskWayPointId },
      skip: !isValidTaskId
    }
  );

  const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE, {
    skip: !isDestination
  });

  const { data: bolInfoData } = useQuery<GetTmsTaskWaypointBolInfoResponse>(GET_TMS_TASK_WAYPOINT_BOL_INFO, {
    variables: { id: isValidTaskId ? taskWayPointId.toString() : '' },
    skip: !isValidTaskId || !isDestination
  });

  const [saveUnloadingDetailInfo, { loading: submitLoading }] = useMutation<{ saveUnloadingDetailInfo: boolean }>(
    SAVE_UNLOADING_DETAIL_INFO
  );

  const [bolRemark, setBolRemark] = useState('');
  const [bolPhotos, setBolPhotos] = useState<string[]>([]);
  const [bolPhotoKeysFromServer, setBolPhotoKeysFromServer] = useState<string[]>([]);
  const [firstPhotoLocation, setFirstPhotoLocation] = useState<FirstPhotoWatermarkLocation | null>(null);
  const { save: saveTmsTaskWaypointLocationInfo } = useSaveTmsTaskWaypointLocationInfo();

  useEffect(() => {
    if (error) {
      Toast.show({
        content: t('pda.common.loadFailed') || 'Failed to load data',
        position: 'center',
        duration: 2000
      });
    }
  }, [error]);

  useEffect(() => {
    if (isDestination) {
      startPrepareCameraLocation();
    }
  }, [isDestination]);

  useEffect(() => {
    const waypoint = bolInfoData?.getTmsTaskWaypointById;
    if (!waypoint) return;
    if (typeof waypoint.bolRemark === 'string') setBolRemark(waypoint.bolRemark);
    if (Array.isArray(waypoint.bolPhotosJson)) setBolPhotoKeysFromServer(waypoint.bolPhotosJson);
    if (Array.isArray(waypoint.bolPhotosUrlsJson) && waypoint.bolPhotosUrlsJson.length > 0) {
      setBolPhotos(waypoint.bolPhotosUrlsJson);
    }
  }, [bolInfoData]);

  if (!state) {
    return null;
  }

  const { backRoute, ...taskData } = state;
  const task = taskData.task;
  const summary = data?.getTmsUnloadingInfoSummary;

  const destinationCodes = summary?.destinationSortingCodes?.join('、') || '-';
  const actualBagCount = summary?.actualBagCount ?? 0;
  const actualParcelQuantity = summary?.actualParcelQuantity ?? 0;
  const containerTypeRows = summary?.containerTypeRows ?? [];
  const destinationGroups = summary?.destinationGroups ?? [];

  const handleRemarkChange = (val: string) => setBolRemark(val.slice(0, 50));

  const handleDeletePhoto = (index: number) => {
    setBolPhotos(prev => prev.filter((_, i) => i !== index));
    if (index < bolPhotoKeysFromServer.length) {
      setBolPhotoKeysFromServer(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleBurstCapture = async () => {
    const currentCount = bolPhotos.length;
    const remaining = Math.max(0, kMaxBolPhotoLength - currentCount);
    const minSelection = currentCount >= kMinBolPhotoLength ? 0 : kMinBolPhotoLength - currentCount;
    const photoTitles = [
      t('pda.pages.tms.arrival.firstPhoto') || '第一张照片',
      t('pda.pages.tms.arrival.secondPhoto') || '第二张照片',
      t('pda.pages.tms.arrival.thirdPhoto') || '第三张照片'
    ];
    const serverTimeMillis = await getCurrentServerTimeMillis(apolloClient);
    await pdaOpenCamera(
      remaining,
      minSelection,
      t('pda.pages.tms.arrival.photoTips') || '',
      photoTitles,
      t('pda.pages.tms.arrival.complete') || '',
      async (newPhotos: string[], loc?: FirstPhotoWatermarkLocation | null) => {
        if (newPhotos?.length > 0) setBolPhotos(prev => [...prev, ...newPhotos]);
        if (loc != null) setFirstPhotoLocation(loc);
      },
      {
        enableWatermark: true,
        ianaTimeZone: warehouseData?.warehouse?.timezoneLocation,
        serverTimeMillis
      }
    );
  };

  const handlePreviousStep = () => {
    navigate(PdaPath.PDA_TMS_UNLOADING, { state: { backRoute, ...taskData } });
  };

  const handleConfirm = async () => {
    if (isDestination && (bolPhotos.length < kMinBolPhotoLength || bolPhotos.length > kMaxBolPhotoLength)) {
      Toast.show({
        content: t('pda.pages.uploadBol.photoCountRequired') || '请上传1～3张BOL照片',
        position: 'center',
        duration: 2000
      });
      return;
    }
    try {
      let bolPhotoKeys: string[] | null = null;
      if (isDestination) {
        const keys: string[] = [...bolPhotoKeysFromServer];
        const newBase64Photos = bolPhotos.filter(p => p && !p.startsWith('http://') && !p.startsWith('https://'));
        if (newBase64Photos.length > 0) {
          const newKeys = await Promise.all(
            newBase64Photos.map(async (base64Photo, index) => {
              const formatted = base64Photo.startsWith('data:') ? base64Photo : `data:image/jpeg;base64,${base64Photo}`;
              const file = await base64ToFile(formatted, `tms_bol_${Date.now()}_${index}.jpg`);
              const result = await fileUpload(file, 'tms_bol_photo');
              return result.objectKey;
            })
          );
          keys.push(...newKeys);
        }
        bolPhotoKeys = keys;
        await saveTmsTaskWaypointLocationInfo(taskId, taskWayPointId, TmsTaskWaypointNodeType.UNLOADING, firstPhotoLocation);
      }

      const { data: submitData } = await saveUnloadingDetailInfo({
        variables: {
          input: {
            taskId,
            taskWaypointId: taskWayPointId,
            bolRemark: isDestination ? (bolRemark.trim() || null) : null,
            bolPhotosJson: bolPhotoKeys,
            operationSource: TmsOperationSourceEnum.PDA,
            addedBagNumbers: [],
            addedTrackingNumbers: [],
            removedBagNumbers: [],
            removedTrackingNumbers: [],
          }
        }
      });

      if (submitData?.saveUnloadingDetailInfo) {
        Toast.show({
          content: t('pda.common.submitSuccess') || '提交成功',
          position: 'center',
          duration: 2000
        });
        if (backRoute) navigate(backRoute);
        else navigate(-1);
      } else {
        throw new Error(t('pda.common.submitFailed') || '提交失败');
      }
    } catch (submitError) {
      const msg = submitError instanceof Error ? submitError.message : String(submitError);
      Toast.show({
        content: `${t('pda.common.submitFailed') || '提交失败'}: ${msg}`,
        position: 'center',
        duration: 2000
      });
    }
  };

  return (
    <div className="w-full h-screen flex flex-col gap-2 bg-gray-50">
      <div className="p-3 flex flex-col gap-4 flex-1 overflow-y-auto pb-20">
        <Header title={t('pda.pages.unloading.step2')} backRoute={backRoute} />
        <div className="flex flex-col gap-3">
          <TmsWaypointStepIndicator currentStep={2} variant="unloading-scan" />

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div>{t('pda.common.loading') || 'Loading...'}</div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* 行1：包牌目的中心 */}
              <div className="flex flex-wrap gap-1 items-baseline">
                <span className="text-sm font-semibold text-black">
                  {t('pda.pages.unloadingDetail.bagDestinationCenter') || '包牌目的中心'}：
                </span>
                <span className="text-sm font-semibold text-purple-600">
                  {destinationCodes}
                </span>
              </div>
              {/* 行2：实卸包牌数 + 实卸运单数 */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <span className="text-sm font-semibold text-black">
                    {t('pda.pages.unloadingDetail.actualBagQuantity') || '实卸包牌数'}：
                  </span>
                  <span className="text-sm font-semibold text-purple-600">
                    {actualBagCount}
                  </span>
                </div>
                <div className="flex-1">
                  <span className="text-sm font-semibold text-black">
                    {t('pda.pages.unloadingDetail.actualWaybillCount') || '实卸运单数'}：
                  </span>
                  <span className="text-sm font-semibold text-purple-600">
                    {actualParcelQuantity}
                  </span>
                </div>
              </div>
              {/* 行3+：应卸容器数 + 容器类型（循环） */}
              {containerTypeRows.map((row, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-black">
                      {t('pda.pages.unloadingDetail.expectedContainerCount') || '应卸容器数'}：
                    </span>
                    <span className="text-sm font-semibold text-purple-600">
                      {row.expectedCount}
                    </span>
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-semibold text-black">
                      {t('pda.pages.loadingDetail.containerTypeLabel') || '容器类型：'}
                    </span>
                    <span className="text-sm font-semibold text-purple-600">
                      {getContainerTypeName(row.containerType)}
                    </span>
                  </div>
                </div>
              ))}

              {/* 按目的中心分组卡片 */}
              {destinationGroups.map((group: TmsUnloadingInfoDestinationGroup, idx: number) => (
                <div key={idx} className="bg-white rounded-lg p-3 shadow-sm flex flex-col gap-2">
                  <div>
                    <span className="bg-purple-500 text-white px-2 py-0.5 rounded text-xs font-medium inline-block">
                      {t('pda.pages.unloadingDetail.bagDestinationCenter') || '目的中心'}:{group.sortingCode || '-'}
                    </span>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1 text-center">
                      <span className="text-sm font-semibold text-black">{t('pda.pages.unloadingDetail.expectedBagQuantity') || '应卸包牌数'}</span>
                    </div>
                    <div className="flex-1 text-center">
                      <span className="text-sm font-semibold text-black">{t('pda.pages.unloadingDetail.actualBagQuantity') || '实卸包牌数'}</span>
                    </div>
                    <div className="flex-1 text-center">
                      <span className="text-sm font-semibold text-black">{t('pda.pages.unloadingDetail.expectedContainerCount') || '应卸容器数'}</span>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1 text-center">
                      <span className="text-sm font-semibold text-black">{group.expectedBagCount}</span>
                    </div>
                    <div className="flex-1 text-center">
                      <span className="text-sm font-semibold text-purple-600">{group.actualBagCount}</span>
                    </div>
                    <div className="flex-1 text-center flex flex-col items-center gap-0.5">
                      {group.containerRows.length > 0 ? group.containerRows.map((row, rIdx) => (
                        <span key={rIdx} className="text-sm font-semibold text-black">
                          {row.expectedCount}/{getContainerTypeName(row.containerType)}
                        </span>
                      )) : <span className="text-sm font-semibold text-black">-</span>}
                    </div>
                  </div>
                </div>
              ))}

              {/* 终点：BOL备注和照片上传 */}
              {isDestination && (
                <div className="bg-white rounded-lg p-4 shadow-sm flex flex-col gap-4">
                  <div className="text-base font-medium text-gray-700">
                    {t('pda.pages.tms.arrival.remark') || '备注'}
                  </div>
                  <TmsCountedInput
                    value={bolRemark}
                    onChange={handleRemarkChange}
                    placeholder={t('pda.pages.tms.arrival.remarkPlaceholder') as string}
                  />
                  <div className="text-base font-medium text-gray-700">
                    {t('pda.pages.uploadBol.bolPhotosTitle') || 'BOL照片 (1~3张)'}
                  </div>
                  <MultiPhotoGalleryView
                    photos={bolPhotos}
                    onDelete={handleDeletePhoto}
                    onNativeBurstCapture={handleBurstCapture}
                    maxDisplayCount={kMaxBolPhotoLength}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div {...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20')}>
        <div className="flex gap-2">
          <Button block color="default" onClick={handlePreviousStep} style={{ flex: 1 }}>
            {t('pda.common.previousStep') || '上一步'}
          </Button>
          <Button
            block
            color="primary"
            loading={submitLoading}
            disabled={isDestination && (bolPhotos.length < kMinBolPhotoLength || bolPhotos.length > kMaxBolPhotoLength)}
            onClick={handleConfirm}
            style={{ flex: 1 }}
          >
            {t('pda.common.confirm') || '确认'}
          </Button>
        </div>
      </div>
    </div>
  );
}
